import { DocumentData, DocumentSnapshot, Timestamp, GeoPoint, FieldValue } from "firebase/firestore";
import { LocationVersion } from "@/types/campLocation";
import { processReference } from "./referenceUtils";
import { ensureFirestoreTimestamp } from "@/utils/firestoreUtils";

export function processVersionData(
  docSnap: DocumentSnapshot<DocumentData>,
  includeId = true
): LocationVersion {
  const data = docSnap.data();
  if (!data) throw new Error("No data found in document");

  // Process timestamp
  const timestamp = data.timestamp instanceof Timestamp 
    ? data.timestamp.toDate().toISOString() 
    : data.timestamp || new Date().toISOString();
    
  // Ensure data.versionData exists (this contains the actual camp location data)
  const versionData = data.versionData || data.data || {};
  
  // Log what we're processing
  console.log(`Processing version ${docSnap.id} with timestamp ${timestamp}`);
  console.log(`Version has name: ${versionData.name || 'undefined'}`);
  
  // Process user data
  let userData = null;
  if (data.user || data.createdBy) {
    // Get raw user data object
    const rawUser = data.user || data.createdBy;
    
    // Log user data for debugging
    console.log("Raw user data for version:", rawUser ? JSON.stringify(rawUser).substring(0, 100) : "null");
    
    // Process user data differently based on its format
    try {
      userData = processReference(rawUser, true);
      
      // Log processed user data for debugging
      console.log("Processed user data:", userData ? JSON.stringify(userData).substring(0, 100) : "null");
    } catch (err) {
      console.error("Error processing user reference:", err);
    }
  }
  
  // Check if _geoloc is stored as GeoPoint and convert it to object with lat/lng for UI display
  if (versionData._geoloc instanceof GeoPoint) {
    console.log("Converting _geoloc GeoPoint to lat/lng object for UI display");
    versionData._geoloc = {
      lat: versionData._geoloc.latitude,
      lng: versionData._geoloc.longitude
    };
  }
  
  // Process timestamp fields in the location data
  if (versionData.createdAt) {
    // Convert createdAt to ISO string for client display
    const createdAtTimestamp = ensureFirestoreTimestamp(versionData.createdAt);
    if (createdAtTimestamp && createdAtTimestamp instanceof Timestamp) {
      versionData.createdAt = createdAtTimestamp.toDate().toISOString();
    } else if (createdAtTimestamp instanceof FieldValue) {
      // ServerTimestamp placeholder - this shouldn't happen in read operations but just in case
      console.warn('[versionDataProcessor] Encountered serverTimestamp placeholder during read operation');
      versionData.createdAt = new Date().toISOString(); // Fallback to current time
    }
  }

  if (versionData.lastEditedAt) {
    // Convert lastEditedAt to ISO string for client display
    const lastEditedAtTimestamp = ensureFirestoreTimestamp(versionData.lastEditedAt);
    if (lastEditedAtTimestamp && lastEditedAtTimestamp instanceof Timestamp) {
      versionData.lastEditedAt = lastEditedAtTimestamp.toDate().toISOString();
    } else if (lastEditedAtTimestamp instanceof FieldValue) {
      // ServerTimestamp placeholder - this shouldn't happen in read operations but just in case
      console.warn('[versionDataProcessor] Encountered serverTimestamp placeholder during read operation');
      versionData.lastEditedAt = new Date().toISOString(); // Fallback to current time
    }
  }
  
  // Process photos array timestamps
  if (versionData.photos && Array.isArray(versionData.photos)) {
    versionData.photos = versionData.photos.map(photo => {
      if (photo && photo.createdAt) {
        const photoTimestamp = ensureFirestoreTimestamp(photo.createdAt);
        if (photoTimestamp instanceof Timestamp) {
          return {
            ...photo,
            createdAt: photoTimestamp.toDate().toISOString()
          };
        } else if (photoTimestamp instanceof FieldValue) {
          // ServerTimestamp placeholder - this shouldn't happen in read operations but just in case
          console.warn('[versionDataProcessor] Encountered serverTimestamp placeholder in photo during read operation');
          return {
            ...photo,
            createdAt: new Date().toISOString() // Fallback to current time
          };
        } else if (photoTimestamp) {
          // Some other valid timestamp format (string, Date, etc.)
          try {
            const date = new Date(photoTimestamp);
            if (!isNaN(date.getTime())) {
              return {
                ...photo,
                createdAt: date.toISOString()
              };
            }
          } catch (error) {
            console.warn('[versionDataProcessor] Could not convert photo timestamp:', photoTimestamp);
          }
        }
      }
      return photo;
    });
  }

  // IMPORTANT: Ensure we're not keeping changeDescription inside the data object
  if (versionData.changeDescription) {
    console.log("Removing changeDescription from version data object");
    delete versionData.changeDescription;
  }
  
  // Process the location data, maintaining direct references
  const processedLocationData = {
    ...versionData,
    // For reading from Firestore, we still need to process these for the UI
    providedByOrg: versionData.providedByOrg ? processReference(versionData.providedByOrg) : null,
    lastEditedBy: versionData.lastEditedBy ? processReference(versionData.lastEditedBy) : null,
    lastPublishedBy: versionData.lastPublishedBy ? processReference(versionData.lastPublishedBy) : null,
  };
  
  // Get version number from data if available
  const versionNumber = data.versionNumber !== undefined ? data.versionNumber : null;
  
  // Create the final version object
  const locationVersion = {
    ...(includeId ? { versionId: docSnap.id } : {}),
    data: processedLocationData,
    locationRef: processReference(data.locationRef),
    timestamp,
    // Make sure to keep the full user object for display
    user: userData,
    action: data.action || 'update',
    changeDescription: data.changeDescription || "",
    status: data.status || "draft",
    versionNumber: versionNumber,
    isNamedVersion: data.isNamedVersion || false
  } as LocationVersion;
  
  return locationVersion;
}
